import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { problems } from '../data/problems';
import CodeViewer from '../components/CodeViewer';
import DescriptionModal from '../components/DescriptionModal';

const difficultyStyles = {
  Easy:   'text-green-400 bg-green-400/10 border border-green-400/20',
  Medium: 'text-yellow-400 bg-yellow-400/10 border border-yellow-400/20',
  Hard:   'text-red-400 bg-red-400/10 border border-red-400/20',
};

const languageStyles = {
  JavaScript: 'text-purple-300 bg-purple-300/10 border border-purple-300/20',
  Java:       'text-orange-300 bg-orange-300/10 border border-orange-300/20',
  SQL:        'text-blue-300 bg-blue-300/10 border border-blue-300/20',
};

const syntaxLanguageMap = {
  JavaScript: 'javascript',
  Java:       'java',
  SQL:        'sql',
};

export default function ProblemPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);

  const problem = problems.find(p => p.id === id);

  if (!problem) {
    return (
      <div className="min-h-screen bg-gray-950 pt-14 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Problem not found.</p>
          <button
            onClick={() => navigate('/')}
            className="text-purple-400 hover:text-purple-300 transition-colors text-sm"
          >
            ← Back to problems
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gray-950 pt-14 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">

          {/* Back button */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-gray-600 hover:text-gray-400 transition-colors mb-6 group text-sm"
          >
            <svg className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to problems
          </button>

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${difficultyStyles[problem.difficulty]}`}>
                {problem.difficulty}
              </span>
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${languageStyles[problem.language]}`}>
                {problem.language}
              </span>
            </div>

            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-3 leading-tight">
              {problem.title}
            </h1>

            <div className="flex flex-wrap gap-2">
              {problem.topics.map(topic => (
                <span key={topic} className="text-xs text-gray-500 bg-gray-800 px-2.5 py-1 rounded-full">
                  {topic}
                </span>
              ))}
            </div>
          </div>

          {/* Description */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 mb-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs font-semibold text-gray-600 uppercase tracking-widest">
                Description
              </h2>
              {problem.fullDescription && (
                <button
                  onClick={() => setModalOpen(true)}
                  className="text-xs text-purple-400 hover:text-purple-300 transition-colors font-medium flex items-center gap-1"
                >
                  Full problem statement
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                </button>
              )}
            </div>
            <p className="text-gray-300 text-sm leading-relaxed">{problem.description}</p>
          </div>

          {/* Solution */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden mb-6">
            <div className="px-5 py-3 border-b border-gray-800 flex items-center justify-between">
              <h2 className="text-xs font-semibold text-gray-600 uppercase tracking-widest">
                Solution
              </h2>
              <span className="text-xs text-gray-700">{problem.language}</span>
            </div>
            <CodeViewer
              code={problem.solution}
              language={syntaxLanguageMap[problem.language]}
            />
          </div>

          {/* LeetCode link */}
          <div className="text-center">
            <a
              href={problem.leetcodeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 font-medium text-sm transition-colors"
            >
              View on LeetCode
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>

        </div>
      </div>

      {/* Description Modal */}
      {modalOpen && (
        <DescriptionModal
          problem={problem}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
}
